import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useBotSettings, useUpdateBotSettings } from "@/hooks/useAITraining";

export default function BotSettingsPanel() {
  const { data: settings, isLoading } = useBotSettings();
  const updateSettings = useUpdateBotSettings();

  const [personality, setPersonality] = useState("");
  const [goal, setGoal] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [model, setModel] = useState("google/gemini-2.5-flash");

  useEffect(() => {
    if (settings) {
      setPersonality(settings.personality || "");
      setGoal(settings.goal || "");
      setAdditionalInfo(settings.additional_info || "");
      setModel(settings.model || "google/gemini-2.5-flash");
    }
  }, [settings]);

  const handleSave = async () => {
    await updateSettings.mutateAsync({
      personality,
      goal,
      additional_info: additionalInfo,
      model
    });
  };

  const estimateTokens = () => {
    const totalText = personality + goal + additionalInfo;
    return Math.ceil(totalText.length / 4);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Model:</label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger className="w-[250px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="google/gemini-2.5-flash">Google Gemini 2.5 Flash</SelectItem>
                <SelectItem value="google/gemini-2.5-pro">Google Gemini 2.5 Pro</SelectItem>
                <SelectItem value="openai/gpt-5">OpenAI GPT-5</SelectItem>
                <SelectItem value="openai/gpt-5-mini">OpenAI GPT-5 Mini</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-muted-foreground">Tokens: ~{estimateTokens()}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium">Personality *</label>
          <p className="text-xs text-muted-foreground mb-2">Define how the AI should speak and behave</p>
          <Textarea 
            value={personality} 
            onChange={(e) => setPersonality(e.target.value)}
            placeholder="You are Circle, a friendly AI assistant..."
            rows={4}
          />
        </div>

        <div>
          <label className="text-sm font-medium">Goal *</label>
          <p className="text-xs text-muted-foreground mb-2">What is the AI's primary objective?</p>
          <Textarea 
            value={goal} 
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Help real estate agents find the right tools..."
            rows={3}
          />
        </div>

        <div>
          <label className="text-sm font-medium">Additional Information *</label>
          <p className="text-xs text-muted-foreground mb-2">Conversation guidelines and rules</p>
          <Textarea 
            value={additionalInfo} 
            onChange={(e) => setAdditionalInfo(e.target.value)}
            placeholder="* Keep responses to 2-4 sentences&#10;* Use agent-specific language&#10;* Never output raw JSON"
            rows={8}
          />
        </div>
      </div>

      <Button onClick={handleSave} disabled={updateSettings.isPending}>
        {updateSettings.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        Save Settings
      </Button>
    </div>
  );
}
